/**
 * GA4 analytics gate for the web app. This is web's own copy of the `shouldTrack()` spine —
 * `site` (../institutional) reimplements the same concept independently, deliberately with no
 * shared package (see docs/architecture.md ## Sprint 21). The GA4 measurement ID is a public
 * constant in code, no env var, no Cloudflare variable, per Brian's ruling.
 */
export const GA_MEASUREMENT_ID = 'G-9K5L7ZY2EQ';
const PROD_HOSTNAME = 'app.getzenhammer.com';

// Matches a full pathname segment that is a UUID (case-insensitive 8-4-4-4-12 hex), e.g. the
// `3fa85f64-5717-4562-b3fc-2c963f66afa6` in `/projects/3fa85f64-5717-4562-b3fc-2c963f66afa6`.
// Anchored to the whole segment so a non-UUID segment is never partially masked.
const UUID_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const shouldTrack = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.location.hostname !== PROD_HOSTNAME) return false;
  if (navigator.doNotTrack === '1' || (window as any).doNotTrack === '1') return false;
  if ((navigator as any).globalPrivacyControl === true) return false;
  return true;
};

let ga4Initialized = false; // idempotency guard — initAnalytics() may be called from an
// effect that re-runs; the <script> tag must only ever be injected once per page load.

// Dedupe guard for trackPageView — holds the dedupe key of the LAST hit this page load actually
// SENT (i.e. shouldTrack() was true and gtag existed). Callers may pass a dedupe key that is
// coarser or finer than the page_location actually sent to GA4 (see trackPageView's docblock).
// AnalyticsPageViewTracker passes the query-stripped-but-UNMASKED location as its key, which is
// what lets /login?code&user -> /login (same key: masking never touched the path; the key is
// unmasked from the start) still dedupe to one hit, while /projects/A -> /projects/B (different
// keys, since IDs aren't masked in the key) correctly sends two, even though both would mask to
// the identical page_location /projects/:id.
let lastSentDedupeKey: string | null = null;

/**
 * Test-only reset for the module-level dedupe guards (`ga4Initialized`, `lastSentDedupeKey`).
 * Mirrors the pattern the existing `initAnalytics` tests use for `ga4Initialized` via
 * `jest.resetModules()` + `require()`, for tests that instead want to reset state on the already
 * `import`-ed module instance.
 */
export const __resetAnalyticsForTests = (): void => {
  ga4Initialized = false;
  lastSentDedupeKey = null;
};

/**
 * Pure. Drops every query key not prefixed `utm_`, which by construction also drops `code`/`user`
 * on `/login` (login.tsx:78) without needing a `/login`-specific special case. Does NOT mask the
 * pathname — callers that need the masked, GA4-safe location should compose this with
 * `maskIdSegments`, or just call `sanitizePageLocation`. Exists as its own export so a dedupe key
 * can be built from the real (unmasked) path — see `trackPageView`'s docblock for why that
 * matters — while still stripping the same PII-bearing query params the sent value strips.
 */
export const stripNonUtmQuery = (origin: string, pathname: string, search: string): string => {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (key.toLowerCase().startsWith('utm_')) kept.append(key, value);
  });
  const qs = kept.toString();
  return `${origin}${pathname}${qs ? `?${qs}` : ''}`;
};

/**
 * Pure. Masks any pathname segment that is itself a UUID (e.g.
 * `/projects/3fa85f64-5717-4562-b3fc-2c963f66afa6` -> `/projects/:id`) so real entity IDs never
 * reach GA4; non-UUID segments, including empty ones from a leading or trailing slash, are left
 * untouched.
 */
export const maskIdSegments = (pathname: string): string =>
  pathname
    .split('/')
    .map((segment) => (UUID_SEGMENT_RE.test(segment) ? ':id' : segment))
    .join('/');

/**
 * Pure and unit-testable on its own — no `window` read inside the function body, so a test can
 * assert stripping behavior with plain string/URLSearchParams inputs. `origin`/`pathname`/
 * `search` are read from `window.location` only at each call site. This is the masked + utm-only
 * value actually SENT to GA4 as `page_location` — see `stripNonUtmQuery`/`maskIdSegments` for the
 * two pieces this composes, and `trackPageView`'s docblock for why the dedupe key is built
 * separately from this rather than reusing it directly.
 */
export const sanitizePageLocation = (origin: string, pathname: string, search: string): string =>
  stripNonUtmQuery(origin, maskIdSegments(pathname), search);

export const initAnalytics = (): void => {
  if (ga4Initialized || !shouldTrack()) return;
  ga4Initialized = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).gtag = function gtag() {
    (window as any).dataLayer.push(arguments);
  };
  (window as any).gtag('js', new Date());
  // gtag.js stamps `dl` on EVERY hit it sends — not just the manual page_view — including
  // sign_up_* custom events and every enhanced-measurement event (scroll, outbound click, form
  // interaction) this stream has enabled. Passing page_location here, sanitized, is what keeps
  // those hits from ever carrying the raw, unsanitized document.location.href (e.g. /login's
  // code=/user= params). send_page_view: false — web has no "first page_view is free from the
  // config call" carve-out the way site does; EVERY page view, including the first, is sent
  // manually by AnalyticsPageViewTracker (its effect fires on mount too).
  const sanitizedCurrentLocation = sanitizePageLocation(
    window.location.origin,
    window.location.pathname,
    window.location.search,
  );
  (window as any).gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    page_location: sanitizedCurrentLocation,
  });
};

export const track = (name: string, params?: Record<string, unknown>): void => {
  if (!shouldTrack() || typeof (window as any).gtag !== 'function') return;
  (window as any).gtag('event', name, params);
};

/**
 * Re-points gtag.js's stored `page_location` before sending the page_view event, so this
 * navigation's hit — and every hit sent after it, until the next call — carries `pageLocation`
 * (the masked, GA4-safe value) rather than the raw `document.location.href` gtag.js would
 * otherwise fall back to. No-op (via `track`'s own `shouldTrack()` gate) when tracking is
 * disabled.
 *
 * Deduped against `dedupeKey` (defaults to `pageLocation` itself), NOT against `pageLocation`
 * directly: if `dedupeKey` is identical to the key this page load last actually SENT, skip the
 * hit. The two are kept separate because `pageLocation` is masked (UUID path segments become
 * `:id`) and masking is lossy — two different real pages, e.g. `/projects/A` and `/projects/B`,
 * mask to the same `page_location` `/projects/:id` but are genuinely different navigations and
 * must both send. Callers that want that distinction preserved pass an unmasked `dedupeKey`
 * (see `AnalyticsPageViewTracker` in App.tsx); the one case this guard still needs to catch —
 * /login consuming a `?code=&user=` reset link, re-running the effect with no real navigation —
 * dedupes correctly either way, since masking never touches the path there. A later call with a
 * different `dedupeKey`, or the same one again after an intervening different one, still sends.
 */
export const trackPageView = (pageLocation: string, dedupeKey: string = pageLocation): void => {
  if (!shouldTrack() || typeof (window as any).gtag !== 'function') return;
  if (dedupeKey === lastSentDedupeKey) return;
  lastSentDedupeKey = dedupeKey;
  (window as any).gtag('set', { page_location: pageLocation });
  track('page_view', { page_location: pageLocation });
};
